import { FC } from "react";
import { Box, Button, Grid, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import { Close } from "@mui/icons-material";
import { PhotoItem } from "../use-patient-form";

type Props = {
  photos: PhotoItem[];
  photoError?: string;
  onAddPhotos: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemovePhoto: (index: number) => void;
};

const photoSrc = (photo: PhotoItem) => (photo instanceof File ? URL.createObjectURL(photo) : photo.imageUrl || "");

/** Aba "Galeria" do modal de paciente (upload + grade de fotos). */
const PatientGalleryTab: FC<Props> = ({ photos, photoError, onAddPhotos, onRemovePhoto }) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
        Adicione fotos do paciente antes e depois de procedimentos, ou qualquer documentação fotográfica relevante.
      </Typography>

      <Button
        component="label"
        fullWidth
        sx={{
          mb: 1,
          height: 80,
          border: `2px dashed ${theme.palette.divider}`,
          borderRadius: "13px",
          color: "text.secondary",
          fontSize: "13.5px",
          fontWeight: 500,
          backgroundColor: "transparent",
          "&:hover": {
            border: `2px dashed ${theme.palette.primary.main}`,
            backgroundColor: theme.palette.brand.cream,
            color: "primary.main",
          },
        }}
      >
        + Adicionar Foto
        <input hidden accept="image/jpeg,image/png,image/webp" multiple type="file" onChange={onAddPhotos} />
      </Button>

      {photoError && (
        <Typography variant="body2" sx={{ color: "error.main", fontSize: "13px", mt: -0.5 }}>
          {photoError}
        </Typography>
      )}

      {photos.length > 0 && (
        <Grid container spacing={1}>
          {photos.map((photo, index) => (
            <Grid item xs={4} key={index}>
              <Box sx={{ position: "relative", paddingBottom: "100%", borderRadius: "13px", overflow: "hidden", border: `1px solid ${theme.palette.divider}` }}>
                <img
                  src={photoSrc(photo)}
                  alt={`Foto ${index + 1}`}
                  style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover" }}
                />
                <Tooltip title="Remover foto" placement="top">
                  <IconButton
                    size="small"
                    aria-label="Remover foto"
                    onClick={() => onRemovePhoto(index)}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      width: 26,
                      height: 26,
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      "&:hover": { backgroundColor: "rgba(255, 255, 255, 1)" },
                    }}
                  >
                    <Close sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default PatientGalleryTab;
